import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { AccountPage } from '@/features/account/pages/account-page';
import { PlanPage } from '@/features/account/pages/plan-page';
import { authReturnPath } from '@/features/auth/auth-return-path';
import { AuthCallbackPage } from '@/features/auth/pages/auth-callback-page';
import { SignInPage } from '@/features/auth/pages/sign-in-page';
import { SignUpPage } from '@/features/auth/pages/sign-up-page';
import { FieldAdminPage } from '@/features/field/pages/field-admin-page';
import { HomePage } from '@/features/home/pages/home-page';
import { PlacementPage } from '@/features/placement/pages/placement-page';
import { EarlierWorkshopsPage } from '@/features/studio/pages/earlier-workshops-page';
import { LessonPage } from '@/features/studio/pages/lesson-page';
import { LessonReferencePage } from '@/features/studio/pages/lesson-reference-page';
import { StudioModulePage } from '@/features/studio/pages/studio-module-page';
import { StudioPage } from '@/features/studio/pages/studio-page';
import { WorkshopEntryPage } from '@/features/workshop/pages/workshop-entry-page';
import { NotFoundPage, RouteError } from '@/shared/ui/route-state';
import { FullPageSpinner } from '@/shared/ui/spinner';

import { AuthenticatedLayout } from './authenticated-layout';

import type { AuthState } from '@/features/auth/auth-context';
import type { QueryClient } from '@tanstack/react-query';

export interface RouterContext {
  auth: AuthState;
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteError,
});

// Public routes apply their own signed-in destination.
const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: Outlet,
});

const signInRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/sign-in',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.status === 'authenticated')
      throw redirect({ to: authReturnPath(search.redirect), replace: true });
  },
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/sign-up',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'authenticated') throw redirect({ to: '/', replace: true });
  },
  component: SignUpPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

// Authenticated area. Anonymous users are sent to sign-in with a return path.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    }
  },
  component: AuthenticatedLayout,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const studioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio',
  component: StudioPage,
});
const earlierWorkshopsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/earlier',
  component: EarlierWorkshopsPage,
});
// Keep bookmarked curriculum URLs working after Program moves into Studio.
const legacyProgramRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/program',
  beforeLoad: () => {
    throw redirect({ to: '/studio', replace: true });
  },
});
const legacyModuleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/program/modules/$number',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/studio/modules/$number', params, replace: true });
  },
});
const legacyWeekRoutes = (['/program/weeks/$number', '/studio/weeks/$number'] as const).map(
  (path) =>
    createRoute({
      getParentRoute: () => appRoute,
      path,
      beforeLoad: ({ params }) => {
        throw redirect({
          to: '/studio/modules/$number',
          params: { number: params.number },
          replace: true,
        });
      },
    }),
);
const workshopRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workshop',
  // Workshop is an activity workspace. Curriculum discovery belongs to Studio.
  component: WorkshopEntryPage,
});
const fieldRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/field',
  component: () => null,
});

const fieldAdminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/field/admin',
  component: FieldAdminPage,
});
const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});
// Stripe returns here with ?checkout=success&session_id=… or ?checkout=cancelled.
const planRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account/plan',
  validateSearch: z.object({
    checkout: z.enum(['success', 'cancelled']).optional(),
    session_id: z.string().optional(),
  }),
  component: PlanPage,
});

// "Change my path": static, so it outranks the activity id route below.
const studioPathRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/path',
  component: PlacementPage,
});
const studioModuleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/modules/$number',
  component: function StudioModuleRouteComponent() {
    const { number } = studioModuleRoute.useParams();
    return <StudioModulePage number={Number(number)} />;
  },
});
const lessonRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/$activityId',
  component: function LessonRouteComponent() {
    const { activityId } = lessonRoute.useParams();
    return <LessonPage activityId={activityId} />;
  },
});
const lessonReferenceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/reference/$code',
  component: function LessonReferenceRouteComponent() {
    const { code } = lessonReferenceRoute.useParams();
    return <LessonReferencePage code={code} />;
  },
});
const workshopDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workshop/$activityId',
  // AuthenticatedLayout owns the persistent editor subtree across curriculum navigation.
  component: () => null,
});

const DesignSystemPage = import.meta.env.DEV
  ? lazy(() =>
      import('@/features/design-system/design-system-page').then((m) => ({
        default: m.DesignSystemPage,
      })),
    )
  : null;
const designSystemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/design-system',
  component: () =>
    DesignSystemPage ? (
      <Suspense fallback={<FullPageSpinner label="Loading component preview…" />}>
        <DesignSystemPage />
      </Suspense>
    ) : null,
});

const ReadingDesignsPage = import.meta.env.DEV
  ? lazy(() =>
      import('@/features/studio/pages/reading-designs-page').then((m) => ({
        default: m.ReadingDesignsPage,
      })),
    )
  : null;
const readingDesignsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/studio/reading-designs',
  component: () =>
    ReadingDesignsPage ? (
      <Suspense fallback={<FullPageSpinner label="Loading reading designs…" />}>
        <ReadingDesignsPage />
      </Suspense>
    ) : null,
});

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([signInRoute, signUpRoute]),
  authCallbackRoute,
  appRoute.addChildren([
    homeRoute,
    ...(import.meta.env.DEV ? [designSystemRoute, readingDesignsRoute] : []),
    legacyProgramRoute,
    legacyModuleRoute,
    ...legacyWeekRoutes,
    studioRoute,
    earlierWorkshopsRoute,
    studioPathRoute,
    studioModuleRoute,
    workshopRoute,
    fieldRoute,
    fieldAdminRoute,
    accountRoute,
    planRoute,
    lessonRoute,
    lessonReferenceRoute,
    workshopDetailRoute,
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({ routeTree, context, defaultPreload: 'intent', scrollRestoration: true });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
